import type { PropsWithChildren } from 'react';

import loadable from '@loadable/component';
import { useContext, useRef } from 'react';

import { ProjectContext } from '@core/context';
import useAskAIState from '@core/hooks/useAskAIState';
import useClassy from '@core/hooks/useClassy';
import useDeferForHydrationMismatch from '@core/hooks/useDeferForHydrationMismatch';
import { useSuperHubStore } from '@core/store';
import classy from '@core/utils/classy';
import { ScrollTargetProvider } from '@core/utils/ScrollTarget';

import classes from './index.module.scss';

const ChatInterfaceComponent = loadable(() => import('@Hub/Owlbot/Chat'), {
  ssr: false,
});

const ChatV2InterfaceComponent = loadable(() => import('@Hub/Owlbot/ChatV2'), {
  ssr: false,
});

export default function ContentWithOwlbot({ children }: PropsWithChildren) {
  const bem = useClassy(classes, 'ContentWithOwlbot');
  const ref = useRef<HTMLDivElement | null>(null);
  const { project } = useContext(ProjectContext);

  const [isOwlbotOpenCached, isInSuperHubAdminView, toggleOwlbot] = useSuperHubStore(s => [
    s.isOwlbotOpenCached,
    s.user.role === 'admin' || s.user.role === 'editor', // SuperHub admin/editor views have taller header
    s.toggleOwlbot,
  ]);

  const { renderMode } = useAskAIState();
  const askAiEnabled = renderMode === 'askai';

  const isOpen = useDeferForHydrationMismatch(isOwlbotOpenCached && askAiEnabled);
  const ChatComponent = project.owlbot?.v2 ? ChatV2InterfaceComponent : ChatInterfaceComponent;

  return (
    <ScrollTargetProvider scrollRef={ref}>
      <div className={bem('&', isOpen && '_with-sidebar', isInSuperHubAdminView && '_superhub')}>
        <div ref={ref} className={bem('-content')}>
          {children}
        </div>

        {!!askAiEnabled && (
          <div className={classy(bem('-sidebar'), 'rm-AskAi-sidebar')}>
            {!!isOpen && <ChatComponent onClose={toggleOwlbot} />}
          </div>
        )}
      </div>
    </ScrollTargetProvider>
  );
}
